import { getAllCompositeSegments, getClipFPS } from '@/player-config/src/utils/clipUtils';
import { Clip } from '@/player-config/src/types/clip-types';
import {
  ClipDeletes,
  CompositeSegment
} from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { mergeDeletes } from '@/player-config/src/utils/assetMetadataUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { approxGTE, approxGT, approxLTE, approxLT } from '@/player-config/src/utils/compareUtils';
import { roundToNDecimalPlaces } from '@/player-config/src/utils/mathUtils';

type Interval = [number, number];

function getInverseIntervals(segmentStart: number, segmentEnd: number, deletes: Interval[]): Interval[] {
  const overlappingDeletes = deletes
    .filter(deleteInterval => approxLTE(deleteInterval[0], segmentEnd) && approxGTE(deleteInterval[1], segmentStart))
    .sort((intervalA, intervalB) => intervalA[0] - intervalB[0]);

  if (overlappingDeletes.length === 0) {
    return approxLT(segmentStart, segmentEnd) ? [[segmentStart, segmentEnd]] : [];
  }

  const intervals: Interval[] = [];
  let currentStart = segmentStart;

  for (const deleteInterval of overlappingDeletes) {
    const [deleteStart, deleteEnd] = deleteInterval;
    if (approxGT(deleteStart, currentStart)) {
      intervals.push([currentStart, Math.min(deleteStart, segmentEnd)]);
    }
    currentStart = Math.max(currentStart, deleteEnd);
    if (approxGTE(currentStart, segmentEnd)) {
      break;
    }
  }
  if (approxLT(currentStart, segmentEnd)) {
    intervals.push([currentStart, segmentEnd]);
  }
  return intervals;
}

function intersectIntervals(intervalsA: Interval[], intervalsB: Interval[]): Interval[] {
  const result: Interval[] = [];
  for (const [aStart, aEnd] of intervalsA) {
    for (const [bStart, bEnd] of intervalsB) {
      const start = Math.max(aStart, bStart);
      const end = Math.min(aEnd, bEnd);
      if (approxLT(start, end)) {
        result.push([start, end]);
      }
    }
  }
  result.sort((intervalA, intervalB) => intervalA[0] - intervalB[0]);
  const merged: Interval[] = [];
  for (const [start, end] of result) {
    if (merged.length > 0 && approxGTE(merged[merged.length - 1][1], start)) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

function mergeIntervals(intervalsA: Interval[], intervalsB: Interval[]): Interval[] {
  const combined: Interval[] = [...intervalsA, ...intervalsB].sort((a, b) => a[0] - b[0]);
  const merged: Interval[] = [];
  for (const [start, end] of combined) {
    if (merged.length > 0 && approxGTE(merged[merged.length - 1][1], start)) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

function intervalsToClipDeletes(intervals: Interval[]): ClipDeletes {
  const clipDeletes: ClipDeletes = {};
  for (const [intervalStart, intervalEnd] of intervals) {
    const key = roundToNDecimalPlaces(intervalStart, 3);
    clipDeletes[String(key)] = {
      bounds: [intervalStart, intervalEnd],
      prev_end_time: intervalStart,
      next_start_time: intervalEnd
    };
  }
  return clipDeletes;
}

/**
 * Synchronizes the asset metadata's `start`, `end`, and `deletes` for a clip,
 * ensuring that facial recognition runs over all necessary portions in custom uploads
 * (when splits are enabled). It intersects undeleted intervals from all composite segments,
 * then updates the asset metadata with the resulting trim and delete ranges.
 *
 * This facilitates running facial recognition (facial rec) over the required timeline
 * regions, abstracting these metadata details for all split segments in the upload.
 *
 * @param {Clip} clip - The clip object whose asset metadata needs to be synced
 */

export function syncAssetMetadataFromSegments(clip: Clip): void {
  if (!isSplitsInTimelineEnabled(clip)) {
    return;
  }

  const compositeSegments = getAllCompositeSegments(clip).filter(
    (seg): seg is CompositeSegment & { segment_metadata: NonNullable<CompositeSegment['segment_metadata']> } =>
      !!seg.segment_metadata
  );

  if (compositeSegments.length < 2) {
    return;
  }

  const globalStart = Math.min(...compositeSegments.map(segment => segment.segment_metadata.start));
  const globalEnd = Math.max(...compositeSegments.map(segment => segment.segment_metadata.end));

  const firstSegmentDeleteIntervals = Object.values(compositeSegments[0].segment_metadata.deletes || {}).map(
    interval => [interval.bounds[0], interval.bounds[1]] as Interval
  );
  let intersectionUndeleted: Interval[] = getInverseIntervals(
    compositeSegments[0].segment_metadata.start,
    compositeSegments[0].segment_metadata.end,
    firstSegmentDeleteIntervals
  );

  for (let i = 1; i < compositeSegments.length; i++) {
    const segment = compositeSegments[i];
    const segmentMetadata = segment.segment_metadata;
    const deletesIntervals = Object.values(segmentMetadata.deletes || {}).map(deleteEntry => deleteEntry.bounds);
    const undeleted = getInverseIntervals(segmentMetadata.start, segmentMetadata.end, deletesIntervals);
    intersectionUndeleted = intersectIntervals(intersectionUndeleted, undeleted);
  }

  const newDeletes = mergeIntervals(getInverseIntervals(globalStart, globalEnd, intersectionUndeleted), [
    [0, globalStart],
    [globalEnd, clip.asset_metadata.maxVideoDuration ?? globalEnd]
  ]);

  const allDeletes = mergeDeletes(intervalsToClipDeletes(newDeletes));
  const duration = (clip.asset_metadata.player_config?.durationInFrames || 0) / getClipFPS(clip);

  clip.asset_metadata.start = globalStart;
  clip.asset_metadata.end = globalEnd;
  clip.asset_metadata.deletes = allDeletes;
  clip.asset_metadata.duration = duration;
}
